class MarketModal {
  constructor(config) {
    this.config = config;
    this.detectedCountry = null;
    this.isCountryAvailable = true;
    // ELIMINAMOS la lista hardcodeada y usamos Intl.DisplayNames
    this.displayNames = new Intl.DisplayNames(['es'], {type: 'region'});
    this.countryToLanguageCode = {
      'ES': 'es-es',  // España - Español
      'FR': 'fr-fr',  // Francia - Francés
      'DE': 'de-de',  // Alemania - Alemán
      'IT': 'it-it',  // Italia - Italiano
      'PT': 'pt-pt',  // Portugal - Portugués
      'NL': 'nl-nl',  // Países Bajos - Holandés
      'BE': 'nl-be',  // Bélgica - Holandés (o 'fr-be' para francés)
      'AT': 'de-at',  // Austria - Alemán
      'PL': 'pl-pl',  // Polonia - Polaco
      'CZ': 'cs-cz',  // República Checa - Checo
      'SK': 'sk-sk',  // Eslovaquia - Eslovaco
      'HU': 'hu-hu',  // Hungría - Húngaro
      'RO': 'ro-ro',  // Rumanía - Rumano
      'BG': 'bg-bg',  // Bulgaria - Búlgaro
      'HR': 'hr-hr',  // Croacia - Croata
      'SI': 'sl-si',  // Eslovenia - Esloveno
      'LT': 'lt-lt',  // Lituania - Lituano
      'LV': 'lv-lv',  // Letonia - Letón
      'EE': 'et-ee',  // Estonia - Estonio
      'FI': 'fi-fi',  // Finlandia - Finés
      'SE': 'sv-se',  // Suecia - Sueco
      'DK': 'da-dk',  // Dinamarca - Danés
      'IE': 'en-ie',  // Irlanda - Inglés
      'LU': 'fr-lu',  // Luxemburgo - Francés (o 'de-lu')
      'MT': 'en-mt',  // Malta - Inglés
      'CY': 'el-cy',  // Chipre - Griego
      'GR': 'el-gr'   // Grecia - Griego
    };
    this.init();
  }

  async init() {
    if (!this.shouldShow()) return;
    
    // PRIMERO detectar el país
    await this.detectCountry();
    
    // LUEGO actualizar el mensaje con el país detectado
    this.updateMessageWithDetectedCountry();
    
    this.setupEventListeners();
    this.openModal();
    
    // Pequeño delay para asegurar que el modal esté renderizado
    setTimeout(() => {
      this.addSVGFlagsToOptions();
      this.preselectDetectedCountry();
    }, 100);
  }

  // NUEVA función para obtener nombre del país automáticamente
  getCountryName(isoCode) {
    try {
      return this.displayNames.of(isoCode.toUpperCase());
    } catch (error) {
      console.warn(`No se pudo obtener el nombre para ${isoCode}:`, error);
      return isoCode; // Fallback al código ISO
    }
  }

  async detectCountry() {
    try {
      const response = await fetch(this.config.apiUrl);
      if (!response.ok) {
        console.warn('No se pudo detectar el país, usando España por defecto');
        this.detectedCountry = { iso: 'ES', name: this.getCountryName('ES') };
        this.isCountryAvailable = true;
        return;
      }

      const data = await response.json();
      const detectedISO = data.detected_values?.country?.handle?.toUpperCase();
      
      console.log('País detectado por IP:', detectedISO);
      
      if (detectedISO) {
        // Obtener el nombre del país automáticamente
        const countryName = this.getCountryName(detectedISO);
        
        // Verificar si está disponible en markets
        if (this.config.markets[detectedISO]) {
          // País detectado está disponible en markets
          this.detectedCountry = { iso: detectedISO, name: countryName };
          this.isCountryAvailable = true;
          console.log('País disponible en markets:', countryName);
        } else {
          // País detectado NO está disponible en markets
          this.detectedCountry = { iso: detectedISO, name: countryName };
          this.isCountryAvailable = false;
          console.log('País NO disponible en markets:', countryName);
        }
      } else {
        // No se pudo detectar país
        this.detectedCountry = { iso: 'ES', name: this.getCountryName('ES') };
        this.isCountryAvailable = true;
      }
    } catch (error) {
      console.warn('Error detectando país:', error);
      this.detectedCountry = { iso: 'ES', name: this.getCountryName('ES') };
      this.isCountryAvailable = true;
    }
  }

  updateMessageWithDetectedCountry() {
    if (!this.detectedCountry) return;

    // CASO 1: País NO disponible - Solo cambiar el texto de descripción
    if (!this.isCountryAvailable) {
      const description = document.querySelector('.market-modal__description');
      if (description) {
        description.innerHTML = `
          We’ve detected you’re browsing from <strong>${this.detectedCountry.name}</strong>. 
          Unfortunately, our services are not available in your region yet. Thank you for your understanding.
        `;
      }
      // Cambiar a España para que el resto funcione normal
      this.detectedCountry = { iso: 'ES', name: this.getCountryName('ES') };
      this.isCountryAvailable = true;
      return;
    }

    // CASO 2: País disponible - Mostrar mensaje normal
    // Actualizar mensaje para un solo mercado
    const singleMarketDescription = document.querySelector('.market-modal__description');
    if (singleMarketDescription && this.config.marketCount === 1) {
      singleMarketDescription.innerHTML = `
        We’ve detected you’re in <strong>${this.detectedCountry.name}</strong>. 
        You’ll be redirected to our store to view local prices and availability.
      `;
    }

    // Actualizar mensaje para múltiples mercados
    if (this.config.marketCount > 1) {
      const multiMarketDescription = document.querySelector('.market-modal__description');
      if (multiMarketDescription) {
        multiMarketDescription.innerHTML = `
          We’ve detected you’re in <strong>${this.detectedCountry.name}</strong>. 
          To provide the best experience, please confirm your region to see accurate pricing, language, and availability.
        `;
      }
    }
  }

  preselectDetectedCountry() {
    if (!this.detectedCountry || !this.isCountryAvailable) return;

    const select = document.getElementById('marketCountry');
    if (select) {
      // Buscar si el país detectado existe en las opciones
      let foundOption = null;
      Array.from(select.options).forEach(option => {
        if (option.value.toUpperCase() === this.detectedCountry.iso) {
          foundOption = option;
        }
      });

      if (foundOption) {
        select.value = foundOption.value;
        console.log('País preseleccionado:', foundOption.value);
        
        // Actualizar también el display customizado
        const contentWrapper = document.querySelector('.custom-select-display > div');
        if (contentWrapper) {
          const flagCode = foundOption.getAttribute('data-flag');
          const cleanName = foundOption.getAttribute('data-clean-name');
          contentWrapper.innerHTML = `<span class="fi fi-${flagCode}" style="width: 20px; height: 15px; display: inline-block; margin-right: 12px; background-size: cover; border-radius: 2px; flex-shrink: 0;"></span>${cleanName}`;
        }
      }
    }
  }

  addSVGFlagsToOptions() {
    const select = document.getElementById('marketCountry');
    if (!select) return;

    console.log('=== AÑADIENDO BANDERAS SVG ===');

    Array.from(select.options).forEach((option, index) => {
      const code = (option.getAttribute('data-country') || option.value || '').toUpperCase();
      if (!code || code === '') return;

      console.log(`Procesando opción ${index}: código="${code}" texto original="${option.textContent}"`);

      // LIMPIAR el nombre: quitar código ISO del principio
      let cleanName = option.textContent.trim()
        .replace(/^[A-Z]{2,3}\s+/, '')  // Quita "ES " o "ESP " del principio
        .replace(/^[A-Z]{2,3}$/, '');   // Si es solo el código, lo quita

      // Si queda vacío, usar Intl.DisplayNames en inglés para el dropdown
      if (!cleanName) {
        try {
          // Usamos 'en' para inglés en el dropdown
          const displayNamesEn = new Intl.DisplayNames(['en'], {type: 'region'});
          cleanName = displayNamesEn.of(code);
        } catch (error) {
          console.warn(`No se pudo obtener el nombre en inglés para ${code}:`, error);
          cleanName = code; // Fallback al código
        }
      }

      option.textContent = cleanName;
      option.setAttribute('data-flag', code.toLowerCase());
      option.setAttribute('data-clean-name', cleanName);

      console.log(`${code} -> ${cleanName}`);
    });

    // Crear select visual customizado
    this.createCustomSelect(select);
  }

  createCustomSelect(originalSelect) {
    // Crear contenedor custom
    const wrapper = document.createElement('div');
    wrapper.className = 'custom-select-wrapper';
    wrapper.style.cssText = 'position: relative; width: 100%; margin-bottom: 2rem;';
    
    // Crear display
    const display = document.createElement('div');
    display.className = 'custom-select-display';
    display.style.cssText = `
      padding: 1.2rem;
      border: var(--inputs-border-width) solid rgba(var(--color-foreground), var(--inputs-border-opacity));
      border-radius: var(--inputs-radius);
      background: rgb(var(--color-background));
      color: rgb(var(--color-foreground));
      cursor: pointer;
      display: flex;
      align-items: center;
      font-family: var(--font-body-family);
      font-size: 1.05em;
      position: relative;
      transition: all 0.2s ease;
      box-shadow: 0 1px 3px rgba(var(--color-shadow), 0.1);
    `;
    
    // Crear contenedor para bandera + texto (ALINEADO A LA IZQUIERDA)
    const contentWrapper = document.createElement('div');
    contentWrapper.style.cssText = `
      display: flex; 
      align-items: center; 
      flex: 1;
      justify-content: flex-start;
    `;
    
    // Crear flecha (POSICIONADA ABSOLUTAMENTE A LA DERECHA)
    const arrow = document.createElement('div');
    arrow.className = 'custom-select-arrow';
    arrow.innerHTML = '▶';
    arrow.style.cssText = `
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 0.8em;
      color: rgba(var(--color-foreground), 0.6);
      transition: transform 0.2s ease;
      pointer-events: none;
      user-select: none;
    `;
    
    // Crear dropdown
    const dropdown = document.createElement('div');
    dropdown.className = 'custom-select-dropdown';
    dropdown.style.cssText = `
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: rgb(var(--color-background));
      border: var(--inputs-border-width) solid rgba(var(--color-foreground), var(--inputs-border-opacity));
      border-top: none;
      border-radius: 0 0 var(--inputs-radius) var(--inputs-radius);
      max-height: 200px;
      overflow-y: auto;
      z-index: 1000;
      display: none;
      box-shadow: 0 4px 6px rgba(var(--color-shadow), 0.1);
    `;

    // Llenar dropdown con opciones
    Array.from(originalSelect.options).forEach(option => {
      if (!option.value) return; // Skip placeholder
      
      const optionDiv = document.createElement('div');
      optionDiv.className = 'custom-select-option';
      optionDiv.style.cssText = `
        padding: 1rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        border-bottom: 1px solid rgba(var(--color-foreground), 0.1);
        transition: background-color 0.2s ease;
      `;
      
      const flagCode = option.getAttribute('data-flag');
      const cleanName = option.getAttribute('data-clean-name');
      
      optionDiv.innerHTML = `<span class="fi fi-${flagCode}" style="width: 20px; height: 15px; display: inline-block; margin-right: 12px; background-size: cover; border-radius: 2px; flex-shrink: 0;"></span>${cleanName}`;
      optionDiv.setAttribute('data-value', option.value);
      
      // Hover effect
      optionDiv.addEventListener('mouseenter', () => {
        optionDiv.style.backgroundColor = 'rgba(var(--color-foreground), 0.05)';
      });
      optionDiv.addEventListener('mouseleave', () => {
        optionDiv.style.backgroundColor = '';
      });
      
      // Click handler
      optionDiv.addEventListener('click', () => {
        // Actualizar select original
        originalSelect.value = option.value;
        
        // Actualizar display
        contentWrapper.innerHTML = `<span class="fi fi-${flagCode}" style="width: 20px; height: 15px; display: inline-block; margin-right: 12px; background-size: cover; border-radius: 2px; flex-shrink: 0;"></span>${cleanName}`;
        
        // Cerrar dropdown
        dropdown.style.display = 'none';
        arrow.style.transform = 'translateY(-50%) rotate(0deg)';
        
        // Trigger change event
        originalSelect.dispatchEvent(new Event('change'));
      });
      
      dropdown.appendChild(optionDiv);
    });

    // Set inicial (será actualizado por preselectDetectedCountry)
    contentWrapper.textContent = '-- Cargando... --';

    // Toggle dropdown
    display.addEventListener('click', () => {
      const isOpen = dropdown.style.display === 'block';
      dropdown.style.display = isOpen ? 'none' : 'block';
      arrow.style.transform = isOpen ? 'translateY(-50%) rotate(0deg)' : 'translateY(-50%) rotate(90deg)';
      
      // Focus effect
      display.style.borderColor = isOpen ? '' : 'rgb(var(--color-button))';
      display.style.boxShadow = isOpen ? '0 1px 3px rgba(var(--color-shadow), 0.1)' : '0 0 0 3px rgba(var(--color-button), 0.1)';
    });

    // Cerrar al hacer click fuera
    document.addEventListener('click', (e) => {
      if (!wrapper.contains(e.target)) {
        dropdown.style.display = 'none';
        arrow.style.transform = 'translateY(-50%) rotate(0deg)';
        display.style.borderColor = '';
        display.style.boxShadow = '0 1px 3px rgba(var(--color-shadow), 0.1)';
      }
    });

    // Hover effect en el display
    display.addEventListener('mouseenter', () => {
      if (dropdown.style.display !== 'block') {
        display.style.borderColor = 'rgba(var(--color-foreground), 0.3)';
      }
    });
    
    display.addEventListener('mouseleave', () => {
      if (dropdown.style.display !== 'block') {
        display.style.borderColor = '';
      }
    });

    // Armar estructura
    display.appendChild(contentWrapper); // Contenido a la izquierda
    display.appendChild(arrow);          // Flecha a la derecha (absoluta)
    wrapper.appendChild(display);
    wrapper.appendChild(dropdown);

    // Ocultar select original y quitar su margin
    originalSelect.style.display = 'none';
    originalSelect.style.marginBottom = '0';
    
    // Insertar después del select original
    originalSelect.parentNode.insertBefore(wrapper, originalSelect.nextSibling);
  }

  shouldShow() {
    const isRoot = location.pathname === '/' || location.pathname === '';
    return isRoot;
  }

  setupEventListeners() {
    const confirmBtn = document.getElementById('marketConfirm');
    const select = document.getElementById('marketCountry');

    confirmBtn?.addEventListener('click', () => {
      this.handleConfirm();
    });

    select?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.handleConfirm();
      }
    });

    const backdrop = document.querySelector('.market-modal__backdrop');
    backdrop?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.shakeModal();
    });

    document.addEventListener('keydown', this.trapFocus.bind(this));
  }

  trapFocus(e) {
    const modal = document.getElementById('marketModalRoot');
    if (!modal || modal.hidden) return;

    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      this.shakeModal();
    }
  }

  shakeModal() {
    const card = document.querySelector('.market-modal__card');
    if (card) {
      card.style.animation = 'none';
      card.offsetHeight;
      card.style.animation = 'modalShake 0.5s ease-in-out';
      
      if (!document.querySelector('#modal-shake-keyframes')) {
        const style = document.createElement('style');
        style.id = 'modal-shake-keyframes';
        style.textContent = `
          @keyframes modalShake {
            0%, 100% { transform: scale(1) translateY(0) translateX(0); }
            25% { transform: scale(1) translateY(0) translateX(-5px); }
            75% { transform: scale(1) translateY(0) translateX(5px); }
          }
        `;
        document.head.appendChild(style);
      }
      
      setTimeout(() => {
        card.style.animation = '';
      }, 500);
    }
  }

  handleConfirm() {
    const select = document.getElementById('marketCountry');
    const iso = select?.value?.toUpperCase();
    
    if (this.config.marketCount > 1 && (!iso || iso === '')) {
      this.showValidationError();
      return;
    }
    
    if (iso) {
      this.redirectToMarket(iso);
    }
  }

  showValidationError() {
    const display = document.querySelector('.custom-select-display') || document.getElementById('marketCountry');
    if (!display) return;

    display.classList.add('error');
    
    const existingError = document.getElementById('validation-error');
    if (existingError) existingError.remove();
    
    const errorMsg = document.createElement('p');
    errorMsg.id = 'validation-error';
    errorMsg.className = 'market-modal__validation-error';
    errorMsg.textContent = 'Por favor, selecciona tu región para continuar';
    display.parentNode.insertBefore(errorMsg, display.nextSibling);
    
    this.shakeModal();
    
    const removeError = () => {
      display.classList.remove('error');
      if (errorMsg) errorMsg.remove();
    };
    
    display.addEventListener('click', removeError, { once: true });
  }

  redirectToMarket(iso) {
    console.log(`Redirigiendo país: ${iso}`);
    
    // Obtener el código idioma-país para la URL
    const languageCountryCode = this.countryToLanguageCode[iso.toUpperCase()];
    
    if (!languageCountryCode) {
      console.warn(`No language-country code found for ${iso}, using es-es as fallback`);
      // Usar España como fallback si no existe el mapeo
      languageCountryCode = 'es-es';
    }
    
    // Construir la URL correcta: https://www.weplant.shop/es-es
    const baseUrl = 'https://www.weplant.shop';
    const destination = `${baseUrl}/${languageCountryCode}`;
    const search = location.search || '';
    
    console.log(`Redirigiendo a: ${destination}${search}`);
    
    const confirmBtn = document.getElementById('marketConfirm');
    if (confirmBtn) {
      confirmBtn.textContent = 'Redirecting...';
      confirmBtn.disabled = true;
      confirmBtn.style.opacity = '0.7';
    }
    
    setTimeout(() => {
      location.replace(destination + search);
    }, 300);
  }

  openModal() {
    const root = document.getElementById('marketModalRoot');
    if (!root) return;

    root.hidden = false;
    document.body.classList.add('market-modal-open');
  }
}

// Inicializar cuando el DOM esté listo
document.addEventListener('DOMContentLoaded', () => {
  if (window.MarketModalConfig) {
    new MarketModal(window.MarketModalConfig);
  }
});
